import AnnualReportForm from "../../../model/AnnualReportForm";
import {useState} from "react";

const RESEARCH_SENSITIVE = `Could your research outputs potentially be considered sensitive or contentious?`;
const HOW_WE_DEFINE_PUBLIC_HEALTH = `Think about whether your findings could be seen as stigmatising or discriminatory 
      toward specific groups — such as people of different ethnicities, genders, socio-economic backgrounds, sexual 
      orientations, or those with disabilities. Also consider if your research might raise broader ethical or social 
      issues, especially if it touches on topics that impact communities in complex or potentially harmful ways.`;
const CONTACT_US_IF_IN_DOUBT = `If you are unsure, please submit a request to our Access Team for further advice.`;
const EXPLAIN_SENSITIVE = `Please explain how your research may be considered sensitive or contentious, and how you plan to manage any associated risks. `;

/**
 * Props for this component
 */
interface IsResearchSensitiveQuestionProps {
   form: AnnualReportForm,
   isLocked: boolean,
}

/**
 * Question regarding the intent to continue using UKB data for health related research.
 *
 * Used in: {@link ApplicantAnnualReportForm|Applicant annual report}
 * @param form An {@link AnnualReportForm} object with a saved report (Could be empty if it's a new report)
 * @param isLocked A boolean to indicate if the report is locked for editing or not
 */
export default function IsResearchSensitiveQuestion({form, isLocked}: IsResearchSensitiveQuestionProps) {
   // State to control the showing/hiding of the text area
   const [inputBoxShowing, setInputBoxShowing] = useState(form.isOutputSensitive);

   return (
      <div id="isOutputSensitive" className="annual-report-question">
         <label id="isOutputSensitiveMainText" className="question-text">{RESEARCH_SENSITIVE}</label>
         <span id="isOutputSensitiveExtraInfo" className="extra-info">{HOW_WE_DEFINE_PUBLIC_HEALTH}</span>
         {isLocked ? (
            <span className="boolean-answer">{form.isOutputSensitive ? 'YES' : 'NOT SURE'}</span>
         ) : (
            //Radio Button Area
            <div id="isOutputSensitiveRadioArea" className="yes-or-no-radio">
               <input id="isOutputSensitiveRadioYesInput" className="form-check-input" type="radio"
                      name="isOutputSensitiveRadioSelection"
                      data-bs-toggle={!inputBoxShowing ? "collapse" : ""}
                      data-bs-target="#isOutputSensitiveTextInput"
                      aria-expanded="false"
                      aria-controls="isOutputSensitiveTextInput"
                      required
                      onChange={() => {
                         form.isOutputSensitive = true;
                         setTimeout(() => {
                            setInputBoxShowing(true);
                         }, 10);
                      }}
                      defaultChecked={(form.annualReportId && form.isOutputSensitive != null) ? (form.isOutputSensitive) : false}
               />
               <label id="isOutputSensitiveRadioYesText" className="form-check-label"
                      htmlFor="isOutputSensitiveRadioYesInput">
                  Yes
               </label>
               <input id="isOutputSensitiveRadioNoInput" className="form-check-input" type="radio"
                      name="isOutputSensitiveRadioSelection"
                      data-bs-toggle={inputBoxShowing ? "collapse" : ""}
                      data-bs-target="#isOutputSensitiveTextInput"
                      aria-expanded="false"
                      aria-controls="isOutputSensitiveTextInput"
                      required
                      onChange={() => {
                         form.isOutputSensitive = false;

                         setTimeout(() => {
                            setInputBoxShowing(false);
                         }, 300);
                      }}
                      defaultChecked={(form.annualReportId && form.isOutputSensitive != null) ? (!form.isOutputSensitive) : false}
               />
               <label id="isOutputSensitiveRadioNoText" className="form-check-label"
                      htmlFor="isOutputSensitiveRadioNoInput">
                  Not sure
               </label>
               <div id="isOutputSensitiveRadioAreaFeedback" className="invalid-feedback">
                  Please select one option.
               </div>
            </div>
         )}
         <span id="isOutputSensitiveExtraInfo2" className="extra-info">{CONTACT_US_IF_IN_DOUBT}</span>
         {/*Text input area*/}
         <div className="accordion accordion-flush" id="isOutputSensitiveAccordion">
            <div id="isOutputSensitiveTextInput"
                 className={`annual-report-question accordion-collapse collapse ${form.isOutputSensitive ? "show" : ""}`}
                 data-bs-parent="#isOutputSensitiveAccordion">
               <span id="isOutputSensitiveResourceText" className="question-text">
                  {EXPLAIN_SENSITIVE}
               </span>
               <textarea id="isOutputSensitiveReasoningInput" name="lastYearResInput" className="form-control" rows={3}
                         disabled={isLocked}
                         onChange={(e) => form.sensitiveAndRiskPlan = encodeURIComponent(e.target.value)}
                         required={inputBoxShowing != null && inputBoxShowing} minLength={100} maxLength={2000}>
         {decodeURIComponent(form.sensitiveAndRiskPlan ? form.sensitiveAndRiskPlan : '')}</textarea>
               <div id="lastYearResScopeFeedback" className="invalid-feedback">
                  Your answer must have between 100 and 2000 characters.
               </div>
            </div>
         </div>
      </div>
   );
}
